import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { acceptInviteSchema, type InviteInfoDto } from "@tc/shared";
import { Button, Icon, LogoIcon, PasswordInput } from "@/components/ui";
import { adminApi, ApiError } from "@/features/clubs/api";

type Phase = "loading" | "form" | "done" | "invalid";

/**
 * Public set-password / first-login page (TW-3436), reached from the invite email link
 * (/invite/:token). Validates the token, lets the recipient choose a password, then sends them
 * to sign in. Sits beside /login — outside the role shells — and renders in the globally
 * initialized language (localStorage → default), like the login page.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = "" } = useParams();

  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<InviteInfoDto | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getInvite(token)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setPhase("form");
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = acceptInviteSchema.safeParse({ password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("invite.error"));
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.acceptInvite(token, parsed.data);
      setPhase("done");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("invite.error"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-stage">
      <div className="auth-shell">
        <div className="auth-head">
          <div className="auth-wordmark">
            <LogoIcon style={{ height: 38 }} />
            <div style={{ textAlign: "left" }}>
              <div className="wm-name">Travel Connection</div>
              <div className="auth-tagline">{t("auth.tagline")}</div>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {phase === "loading" && <p className="auth-sub">{t("invite.loading")}</p>}

          {phase === "invalid" && (
            <>
              <h1>{t("invite.title")}</h1>
              <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
                <Icon name="alert" size={17} className="aa-ic" />
                <div className="aa-body">{t("invite.invalid")}</div>
              </div>
              <div className="auth-foot" style={{ marginTop: 18 }}>
                <button type="button" className="link" onClick={() => navigate("/login")}>
                  {t("invite.goToLogin")}
                </button>
              </div>
            </>
          )}

          {phase === "form" && (
            <>
              <h1>{t("invite.title")}</h1>
              <p className="auth-sub">{t("invite.subtitle", { name: info?.name ?? "" })}</p>

              {error && (
                <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
                  <Icon name="alert" size={17} className="aa-ic" />
                  <div className="aa-body">{error}</div>
                </div>
              )}

              <form className="auth-form" onSubmit={onSubmit} noValidate>
                <div>
                  <label className="auth-field-label" htmlFor="invite-password">
                    {t("invite.password")}
                  </label>
                  <PasswordInput
                    id="invite-password"
                    icon="lock"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    showLabel={t("auth.showPassword")}
                    hideLabel={t("auth.hidePassword")}
                  />
                  <div className="field-hint" style={{ marginTop: 6 }}>
                    {t("invite.passwordHint")}
                  </div>
                </div>

                <Button type="submit" size="lg" full disabled={submitting}>
                  {submitting ? t("auth.signingIn") : t("invite.submit")}
                </Button>
              </form>
            </>
          )}

          {phase === "done" && (
            <>
              <h1>{t("invite.success")}</h1>
              <p className="auth-sub">{t("invite.successBody")}</p>
              <Button size="lg" full style={{ marginTop: 22 }} onClick={() => navigate("/login")}>
                {t("invite.goToLogin")}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="auth-pagefoot">
        <span>© {new Date().getFullYear()} Travel Connection Spain</span>
      </div>
    </main>
  );
}
